import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchQualityChapters,
  fetchQualityChapter,
  updateQualityChapter,
  createQualityChapter,
  deleteQualityChapter,
} from "../../api/quality/quality_chapter";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  qualitiesWithoutChapter: [],
  totalCount: 0,
  currentCount: 0,
  newCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteQualityChapterAsync = createAsyncThunk(
  "chapters/deleteQualityChapterAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteQualityChapter(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_quality_chapter");
    }
  }
);

export const createQualityChapterAsync = createAsyncThunk(
  "chapters/createQualityChapterAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createQualityChapter(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_quality_chapter");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_quality_chapter");
    }
  }
);

export const updateQualityChapterAsync = createAsyncThunk(
  "chapters/updateQualityChapterAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateQualityChapter(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_quality_chapter");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_quality_chapter");
    }
  }
);

export const getQualityChaptersAsync = createAsyncThunk(
  "chapters/getQualityChaptersAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchQualityChapters(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_quality_chapters");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_quality_chapters");
    }
  }
);

export const getQualityChapterAsync = createAsyncThunk(
  "chapters/getQualityChapterAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchQualityChapter(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_quality_chapter");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_quality_chapter");
    }
  }
);

const qualityChapterSlice = createSlice({
  name: "chapters",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getQualityChaptersAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getQualityChaptersAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data || [];
        state.qualitiesWithoutChapter = action.payload.qualitiesWithoutChapter || [];
        state.totalCount = action.payload.totalCount || 0;
        state.currentCount = action.payload.currentCount || 0;
        state.newestQualityChaptersCount = action.payload.newCount || 0;
      })
      .addCase(getQualityChaptersAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente kvalitetskapitlene.");
      })
      .addCase(updateQualityChapterAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateQualityChapterAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (type) => type.id === action.payload.id
          );
          draftState.data[index] = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateQualityChapterAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere kvalitetskapittelet.");
      })
      .addCase(createQualityChapterAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createQualityChapterAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createQualityChapterAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette kvalitetskapittelet.");
      })
      .addCase(deleteQualityChapterAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteQualityChapterAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(deleteQualityChapterAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette kvalitetskapittelet.");
      })
      .addCase(getQualityChapterAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getQualityChapterAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.chapter = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getQualityChapterAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente kvalitetskapittelet.");
      });
  },
});

export const { reducer } = qualityChapterSlice;

export default qualityChapterSlice;
